/**
 * Fetch condicional con ETag + Last-Modified — workaround para Cloudflare
 * (preview) que sobrescribe ``Cache-Control`` a ``no-store``.
 *
 * Uso:
 *   const { data, fromCache } = await conditionalGet("/content/home");
 *
 * Cómo funciona:
 *  1. La primera llamada a una URL hace GET normal y guarda ``etag``,
 *     ``lastModified`` y el body en un ``Map`` module-level.
 *  2. Las llamadas siguientes envían ``If-None-Match`` (y
 *     ``If-Modified-Since`` como fallback). Si el servidor responde 304,
 *     devolvemos el body cacheado sin re-descargarlo.
 *  3. Si el servidor responde 200 con un ETag distinto, actualizamos el
 *     cache y devolvemos el nuevo body.
 *
 * axios rechaza 304 por defecto — pasamos ``validateStatus`` custom para
 * aceptarlo. En 304 la respuesta llega con body vacío y usamos la copia
 * en memoria.
 */
import { api } from "./api";

const _cache = new Map(); // url -> {etag, lastModified, data, fetchedAt}

/**
 * @param {string} url path relativo al ``/api`` base (ej. "/content/home").
 * @param {object} [axiosConfig] opciones extra para axios (rara vez necesario).
 * @returns {Promise<{data:any, fromCache:boolean}>}
 */
export async function conditionalGet(url, axiosConfig = {}) {
  const cached = _cache.get(url);
  const headers = { ...(axiosConfig.headers || {}) };
  if (cached?.etag) headers["If-None-Match"] = cached.etag;
  if (cached?.lastModified) headers["If-Modified-Since"] = cached.lastModified;

  const res = await api.get(url, {
    ...axiosConfig,
    headers,
    validateStatus: (s) => s === 304 || (s >= 200 && s < 300),
  });

  if (res.status === 304 && cached) {
    cached.fetchedAt = Date.now();
    return { data: cached.data, fromCache: true };
  }

  // 200 (o 304 sin cache previo — edge case defensivo).
  const etag = res.headers?.etag || res.headers?.ETag || null;
  const lastModified =
    res.headers?.["last-modified"] || res.headers?.["Last-Modified"] || null;
  _cache.set(url, {
    etag,
    lastModified,
    data: res.data,
    fetchedAt: Date.now(),
  });
  return { data: res.data, fromCache: false };
}

/**
 * Invalida el cache condicional para una URL concreta o para todas.
 * Útil tras un save admin (`PUT /admin/content/*`) para forzar 200 fresh
 * en el siguiente fetch, aunque el ETag anterior aún fuese válido.
 */
export function invalidateConditional(url) {
  if (url) _cache.delete(url);
  else _cache.clear();
}
